// Create Folder Modal
import React, { useState } from 'react';
import { X, Folder } from 'lucide-react';
import { useFileStore } from '@stores/useFileStore';
import { useToast } from '@components/ui';
import { SUSPENSION_ERROR_MESSAGE } from '@utils/suspension';

interface CreateFolderModalProps {
  onClose: () => void;
  onSuccess?: () => void;
  parentId?: string;
}

export const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  onClose,
  onSuccess,
  parentId,
}) => {
  const [folderName, setFolderName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { createFolder } = useFileStore();
  const { success: showSuccess, error: showError } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (folderName.trim() === '') return;

    setIsLoading(true);
    setError(null);
    try {
      console.log('📁 CreateFolderModal: Creating folder:', folderName, 'parentId:', parentId, 'type:', typeof parentId);
      const folder = await createFolder(folderName.trim(), parentId);
      console.log('✅ CreateFolderModal: Folder created:', folder, 'parentId in response:', folder?.parentId);
      showSuccess('Folder created', `"${folderName}" has been created successfully.`);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to create folder:', err);
      const error = err instanceof Error ? err : new Error('Failed to create folder');
      
      // Check if it's a suspension error
      if (error.message === 'ACCOUNT_SUSPENDED' || error.message.includes('ACCOUNT_SUSPENDED')) {
        showError(SUSPENSION_ERROR_MESSAGE.title, SUSPENSION_ERROR_MESSAGE.message);
        setError(SUSPENSION_ERROR_MESSAGE.message);
      } else {
        const errorMsg = error.message || 'Failed to create folder';
        setError(errorMsg);
        showError('Failed to create folder', errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-3">
      <div className="w-full max-w-sm sm:max-w-md bg-dark-secondary/95 backdrop-blur-md border border-white/10 rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-white/10">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-primary/20 rounded-lg">
              <Folder className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            </div>
            <h2 className="font-dela text-base sm:text-lg text-white">New Folder</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 sm:p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all flex-shrink-0"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-3 sm:p-4">
          <div className="mb-4">
            <label
              htmlFor="folder-name"
              className="block mb-1.5 font-inter font-bold text-xs sm:text-sm text-white/80"
            >
              Folder Name
            </label>
            <input
              id="folder-name"
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-inter focus:outline-none focus:border-primary transition-colors"
              placeholder="Enter folder name"
              autoFocus
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-3 p-2 sm:p-2.5 bg-danger/20 border border-danger rounded-lg">
              <p className="font-inter text-xs sm:text-sm text-danger">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg font-inter font-bold transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || folderName.trim() === ''}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-[#30d870]/80 to-[#00ff61]/80 hover:from-[#30d870] hover:to-[#00ff61] text-black text-sm rounded-lg font-inter font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating...' : 'Create Folder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

